// Bestand lesen: Übersicht Artikel × Standort, ein Artikel im Einzelnen, Bewegungsjournal, Kennzahlen der Startseite.
// Alle Zahlen stammen aus dem Bestandskern (ledger.levels/balances/journal …) – dieses Modul rechnet keinen Bestand.
import { PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput, zListPage } from '@shared/list'
import { z } from 'zod'
import { zDay, zDocumentRef, zLocationRef, zUnitInfo } from './common'

const READ = 'inventory.stock.read'

export const zStockRow = z.object({
  /** `${itemId}:${locationId}` */
  id: z.string(),
  itemId: zId,
  sku: z.string(),
  name: z.string(),
  groupId: zId.nullable(),
  groupName: z.string().nullable(),
  locationId: zId,
  locationCode: z.string(),
  locationName: z.string(),
  /** Gebinde, in dem der Bestand gezeigt wird (Lagereinheit, sonst Verkaufsgebinde) */
  unit: zUnitInfo,
  /** Kurzform der Basiseinheit: „Fl“ */
  baseUnit: z.string(),
  /** alle Mengen in Tausendstel der Basiseinheit */
  onHand: z.int(),
  blocked: z.int(),
  reserved: z.int(),
  available: z.int(),
  /** offene Bestellmengen (Einkauf) */
  incoming: z.int(),
  /** unterwegs aus Umlagerungen zu diesem Standort */
  inTransit: z.int(),
  reorderQty: z.int(),
  targetQty: z.int(),
  listed: z.boolean(),
  belowReorder: z.boolean(),
  /** nächstes Mindesthaltbarkeitsdatum einer Charge mit Bestand an diesem Standort */
  nextExpiry: z.object({ lotId: zId, lotNo: z.string(), bestBefore: z.string(), daysLeft: z.int(), qty: z.int() }).nullable(),
  /** Lagerwert zum gleitenden Durchschnitt – nur mit „Einstandspreise sehen“ */
  valueCents: z.int().optional(),
})
export type StockRow = z.output<typeof zStockRow>

export const stockList = defineContract({
  name: 'inventory.stock.list',
  title: 'Bestand anzeigen',
  description: 'Bestand je Artikel und Standort: vorhanden, reserviert, verfügbar, unterwegs, Melde- und Zielbestand.',
  llm: {
    description:
      'Listet den Bestand je Artikel UND Standort (eine Zeile je Paar) an den Standorten, die der Nutzer sehen darf. ' +
      'Mengen in Tausendstel der Basiseinheit (1 Stück = 1000); `unit.baseQty` rechnet in das Lagergebinde um ' +
      '(Karton mit 20 Stück = 20000). `available` = vorhanden − gesperrt − reserviert, `inTransit` = unterwegs aus ' +
      'Umlagerungen zu diesem Standort. Filter: Standort, Warengruppe, „unter Meldebestand“, Suchtext ' +
      '(Name, Artikelnummer). Verwenden für „Was fehlt in Ismaning?“ oder „Wo ist … knapp?“. Für EINEN Artikel mit ' +
      'Lagerplätzen inventory.stock.item, für Bewegungen inventory.stock.journal. Bucht nichts.',
    keywords: ['Bestand', 'Lagerbestand', 'verfügbar', 'Meldebestand', 'Zielbestand', 'knapp', 'ausverkauft', 'Nachschub', 'unterwegs', 'Lagerwert'],
  },
  input: listInput({
    sortBy: ['name', 'sku', 'available', 'onHand', 'coverage'],
    defaultSort: { by: 'name' },
    filters: {
      locationId: zId.optional(),
      groupId: zId.optional(),
      /** nur Zeilen unter dem Meldebestand */
      belowReorder: z.boolean().optional(),
      /** nur Zeilen mit einer Charge, deren MHD in `expiringDays` Tagen erreicht ist (oder abgelaufen) */
      expiring: z.boolean().optional(),
      expiringDays: z.int().min(0).max(365).optional(),
      /** auch Paare ohne Bestand und ohne Disposition zeigen (Standard: nur gelistete oder bestandsführende) */
      includeEmpty: z.boolean().optional(),
    },
  }),
  output: listOutput(zStockRow),
  permission: READ,
  risk: 'read',
  scope: 'any',
  fieldPermissions: { 'rows.*.valueCents': PERMISSION_COSTS_READ },
})

const zLocationStock = z.object({
  location: zLocationRef,
  onHand: z.int(),
  blocked: z.int(),
  reserved: z.int(),
  available: z.int(),
  incoming: z.int(),
  inTransit: z.int(),
  minQty: z.int(),
  reorderQty: z.int(),
  targetQty: z.int(),
  listed: z.boolean(),
  belowReorder: z.boolean(),
})

const zBalance = z.object({
  locationId: zId,
  binId: zId,
  binCode: z.string(),
  /** pick | bulk | receiving | blocked | empties … */
  binKind: z.string(),
  lotId: zId.nullable(),
  lotNo: z.string().nullable(),
  bestBefore: z.string().nullable(),
  /** Tage bis zum Datum (negativ = abgelaufen) */
  daysLeft: z.int().nullable(),
  /** ok | blocked | recall | expired */
  lotStatus: z.string().nullable(),
  qty: z.int(),
  /** Sperrplatz oder gesperrte Charge */
  blocked: z.boolean(),
})

export const stockItem = defineContract({
  name: 'inventory.stock.item',
  title: 'Bestand eines Artikels anzeigen',
  description: 'Ein Artikel über alle sichtbaren Standorte: Bestand, Transit und Lagerplätze.',
  llm: {
    description:
      'Liefert für EINEN Artikel den Bestand je Standort (vorhanden, gesperrt, reserviert, verfügbar, bestellt, ' +
      'unterwegs, Melde-/Zielbestand) und darunter der Bestand je Lagerplatz. Mengen in ' +
      'Tausendstel der Basiseinheit. Verwenden für „Wie viel … haben wir und wo liegt es?“ und um den Lagerplatz für ' +
      'eine Korrektur zu finden. Für die Bewegungen des Artikels inventory.stock.journal.',
    keywords: ['Bestand', 'Artikel', 'Lagerplatz', 'wo liegt', 'wie viel', 'Sperrbestand'],
  },
  input: z.strictObject({ itemId: zId }),
  output: z.object({
    item: z.object({
      id: zId,
      sku: z.string(),
      name: z.string(),
      groupName: z.string().nullable(),
      baseUnit: z.string(),
      unit: zUnitInfo,
      units: z.array(zUnitInfo),
      lotTracked: z.boolean(),
      /** none | best_before | use_by */
      dateTracking: z.string(),
      active: z.boolean(),
    }),
    locations: z.array(zLocationStock),
    balances: z.array(zBalance),
    totals: z.object({ onHand: z.int(), blocked: z.int(), reserved: z.int(), available: z.int(), incoming: z.int(), inTransit: z.int() }),
    /** Bewertung – nur mit „Einstandspreise sehen“ */
    valuation: z
      .object({
        valueCents: z.int(),
        /** gleitender Durchschnitt: Cent je `avgPerQty` Tausendstel der Basiseinheit */
        avgCostCents: z.int().nullable(),
        avgPerQty: z.int().nullable(),
      })
      .optional(),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
  fieldPermissions: { valuation: PERMISSION_COSTS_READ },
})

export const MOVEMENT_KINDS = [
  'opening',
  'receipt',
  'purchase_return',
  'sale',
  'sale_return',
  'transfer_out',
  'transfer_in',
  'adjustment',
  'count',
  'revaluation',
  'empties_in',
  'empties_out',
  'assembly',
] as const

export const zJournalRow = z.object({
  id: zId,
  at: z.date(),
  businessDate: z.string(),
  movementType: z.string(),
  /** deutsch: „Wareneingang“, „Umlagerung Ausgang“ … */
  typeLabel: z.string(),
  itemId: zId,
  sku: z.string(),
  itemName: z.string(),
  location: zLocationRef,
  binCode: z.string(),
  lotId: zId.nullable(),
  lotNo: z.string().nullable(),
  /** ± Menge in Tausendstel der Basiseinheit */
  qty: z.int(),
  baseUnit: z.string(),
  /** ± Menge im erfassten Gebinde (Tausendstel) – so, wie die Person gedacht hat; null, wenn nur die Basismenge feststeht */
  unitQty: z.int().nullable(),
  unit: z.string().nullable(),
  /** Bestand des Artikels am Standort NACH dieser Bewegung (Basiseinheit) – nur wenn nach einem Artikel gefiltert ist */
  balanceAfter: z.int().nullable(),
  document: zDocumentRef.nullable(),
  reason: z.string().nullable(),
  note: z.string().nullable(),
  userName: z.string().nullable(),
  /** ui | assistant | api | system … */
  channel: z.string(),
  isReversal: z.boolean(),
  /** ± Einstandswert – nur mit „Einstandspreise sehen“ */
  valueCents: z.int().optional(),
})

export const stockJournal = defineContract({
  name: 'inventory.stock.journal',
  title: 'Bewegungsjournal anzeigen',
  description: 'Unveränderliches Journal der Bestandsbewegungen, jüngste zuerst – mit Beleg, Grund und Bestand danach.',
  llm: {
    description:
      'Liefert Bestandsbewegungen, jüngste zuerst: Zeitpunkt, Art (Wareneingang, Verkauf, Umlagerung, Korrektur, ' +
      'Zählung …), Artikel, Standort, Menge mit Vorzeichen in Tausendstel der Basiseinheit (und im erfassten ' +
      'Gebinde), Beleg, Grund und Person. Mit `itemId` steht in `balanceAfter` der Bestand nach der Bewegung. ' +
      'Verwenden für „Warum fehlen …?“, „Wer hat … ausgebucht?“. Nur lesen – ' +
      'gebucht wird über Wareneingang, Umlagerung oder Korrekturbuchung.',
    keywords: ['Journal', 'Bewegungen', 'Historie', 'Zugang', 'Abgang', 'warum fehlt', 'wer hat', 'Buchung'],
  },
  input: z.strictObject({
    itemId: zId.optional(),
    locationId: zId.optional(),
    lotId: zId.optional(),
    documentId: zId.optional(),
    movementTypes: z.array(z.enum(MOVEMENT_KINDS)).max(13).optional(),
    from: zDay.optional(),
    to: zDay.optional(),
    page: zListPage,
  }),
  output: listOutput(zJournalRow),
  permission: READ,
  risk: 'read',
  scope: 'any',
  fieldPermissions: { 'rows.*.valueCents': PERMISSION_COSTS_READ },
})

/** Zähler der Seitenleiste: Umlagerungen, bei denen der Nutzer am Zug ist. */
export const transferTodoCount = defineContract({
  name: 'inventory.transfer.todo',
  title: 'Offene Umlagerungen zählen',
  description: 'Zahl der Umlagerungen, die auf eine Handlung des Nutzers warten (freigeben, versenden, empfangen).',
  llm: {
    description:
      'Zählt Umlagerungen, bei denen der Nutzer am Zug ist: angeforderte, die er freigeben darf, freigegebene, die er ' +
      'versenden darf, und versandte, die er empfangen darf. Für die Liste selbst inventory.transfer.list.',
    keywords: ['Umlagerung', 'offen', 'wartet', 'freigeben', 'empfangen'],
  },
  input: z.strictObject({}),
  output: z.object({ count: z.int() }),
  permission: 'inventory.transfer.read',
  risk: 'read',
  scope: 'any',
  assistant: 'never',
})

/** Zähler der Seitenleiste: Chargen, deren MHD in 14 Tagen erreicht oder überschritten ist. */
export const lotCriticalCount = defineContract({
  name: 'inventory.lot.critical',
  title: 'Kritische Chargen zählen',
  description: 'Zahl der Chargen mit Bestand, deren Mindesthaltbarkeit in 14 Tagen erreicht oder überschritten ist.',
  llm: {
    description: 'Zählt Chargen mit Bestand an den Standorten des Nutzers, deren MHD in 14 Tagen erreicht oder abgelaufen ist. Für die Liste inventory.lot.list.',
    keywords: ['MHD', 'Charge', 'kritisch', 'läuft ab'],
  },
  input: z.strictObject({}),
  output: z.object({ count: z.int() }),
  permission: 'inventory.lot.read',
  feature: 'lots',
  risk: 'read',
  scope: 'any',
  assistant: 'never',
})

/** Wohin ein Lagerbeleg in der Oberfläche führt (Suchtreffer, Aufgaben und Verweise kennen nur die Kennung). */
export const documentLocate = defineContract({
  name: 'inventory.document.locate',
  title: 'Lagerbeleg finden',
  description: 'Nennt zu einem Lagerbeleg die Belegart – und zu einem Umlagerungs-Eingang seine Umlagerung.',
  llm: {
    description: 'Liefert zu einer Belegkennung die Belegart (goods_receipt, transfer, transfer_receipt, stock_adjustment) und bei einem Umlagerungs-Eingang die Kennung der Umlagerung.',
    keywords: ['Beleg'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({ id: zId, type: z.string(), number: z.string().nullable(), transferId: zId.nullable() }),
  permission: READ,
  risk: 'read',
  scope: 'any',
  assistant: 'never',
})
